/**
 * Client-side image shrinking for profile photos.
 *
 * The profile-photo upload failed with "timeout of 30000ms exceeded". The
 * server takes 0.11s for a 4000x3000 JPEG (measured against the view directly),
 * so the ceiling was transport: `services/api.ts` sets a 30s global axios
 * timeout, and a multi-megabyte phone photo on a slow uplink does not finish
 * its request body inside it.
 *
 * Raising the timeout alone would only make people wait longer. Shrinking
 * before upload removes the cause: a typical 8MB phone photo becomes a couple
 * of hundred KB, which is far more image than a 96px avatar needs.
 */

/** nginx `client_max_body_size` on this vhost. Rejecting here names the limit
 *  instead of letting nginx answer 413 after the whole body has been sent. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Files at or below this go untouched — re-encoding them would cost quality
 *  for no meaningful bandwidth saving. */
const SKIP_BELOW_BYTES = 512 * 1024;

export function isTooLarge(file: File): boolean {
  return file.size > MAX_UPLOAD_BYTES;
}

/**
 * Bound `file` to `maxEdge` on its longest side and re-encode as JPEG.
 *
 * Returns the ORIGINAL file whenever shrinking would not help or is not
 * possible (already small, already within bounds, no 2D context, encode
 * failed). Uploading the original is always better than uploading nothing.
 */
export async function downscaleImage(
  file: File,
  maxEdge = 1024,
  quality = 0.85,
): Promise<File> {
  if (file.size <= SKIP_BELOW_BYTES) return file;

  const image = await loadImage(file);
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  if (scale >= 1) return file;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) return file;

  const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
  return new File([blob], name, { type: 'image/jpeg' });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}
